import { useIncidentFileUpload } from '../hooks/useIncidentFileUpload'
import { useLightMode } from '@/hooks/useLightMode'
import { useOpenIncident } from '@/hooks/useOpenIncident'
import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import * as Sentry from '@sentry/nextjs'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Modal } from '@hivewatch/dark'

import { useCreateIncidentMutation } from '@/graphql/generated/hooks'
import { IncidentStatus, IncidentType } from '@/graphql/generated/schemas'
import { trackEvent } from '@/utils/analytics'

import { FormInputsIProps } from '../types/types'
import { INCIDENT_CREATE_ANALYTICS } from '../utils/constants'
import { getRefetchQueries } from '../utils/refetchQueries'
import { DarkIncidentModalBody } from './DarkIncidentModalBody'

interface DarkCreateIncidentModalIProps {
  isOpen: boolean
  onClose: () => void
}

/**
 * Rebrand variant of {@link CreateIncidentModal} built on `@hivewatch/dark`.
 * The mutation, the refetch set, the file-upload step and every Mixpanel event
 * fire exactly as in the legacy modal.
 */
export const DarkCreateIncidentModal = ({
  isOpen,
  onClose,
}: DarkCreateIncidentModalIProps) => {
  const { t } = useTranslation()
  const colorScheme = useLightMode()
  const { shouldEnableCreateIncident } = usePermissions()
  const [createIncident, { loading: isMutationLoading }] =
    useCreateIncidentMutation()
  const { openIncident } = useOpenIncident()

  const form = useForm<FormInputsIProps>({
    mode: 'onChange',
    reValidateMode: 'onChange',
  })

  const { formState } = form
  const { isValid, isDirty } = formState
  const { showSuccess, showError } = useToasts()

  const {
    selectedFiles,
    handleFileSelect,
    removeFile,
    clearFiles,
    uploadFiles,
    isUploading: isUploadingFiles,
  } = useIncidentFileUpload()

  const isLoading = isMutationLoading || isUploadingFiles

  const onSubmit: SubmitHandler<FormInputsIProps> = async (
    values: FormInputsIProps
  ) => {
    if (!values) {
      return
    }

    const address = values.address?.value ? [values.address?.value] : null
    const latitude = values.latitude
      ? Number(values.latitude)
      : values.address?.latitude ?? null
    const longitude = values.longitude
      ? Number(values.longitude)
      : values.address?.longitude ?? null

    const incidentCategoryId: string = values.incidentCategory?.value
    const incidentCategoryName: string = values.incidentCategory?.label

    // DeviceHealth incidents are the only incident category that have specified
    // device, so the type can be inferred from the deviceOrigin form field
    const incidentType =
      values.deviceOrigin === undefined ? null : IncidentType.DeviceHealth

    // If name isn't given, fallback to category
    const nameToUse: string = values.name ? values.name : incidentCategoryName

    try {
      const fileIds = await uploadFiles()

      const data = {
        name: nameToUse,
        type: incidentType,
        facilityId: values.facilityOrigin?.value,
        floorId: values.floorOrigin?.value,
        deviceIds: [values.deviceOrigin?.value],
        status: IncidentStatus.Active,
        description: values.description ? values.description : null,
        manualIncidentCategoryId: incidentCategoryId,
        address,
        latitude,
        longitude,
        imageIds: fileIds.length > 0 ? fileIds : undefined,
      }

      const refetchQueries = getRefetchQueries(values.floorOrigin?.value)
      const dataResponse = await createIncident({
        variables: {
          input: data,
        },
        refetchQueries,
      })

      trackEvent(INCIDENT_CREATE_ANALYTICS.incidentCreated, {
        incident_id: dataResponse?.data.createIncident.incident.id,
        incident_status: data.status,
        incident_source: values.deviceOrigin?.label,
        device_id: values.deviceOrigin?.value,
        device_type: values.type?.label,
        has_images: fileIds.length > 0,
        image_count: fileIds.length,
      })

      clearFiles()

      onClose()
      showSuccess(t('incidents:incident_created'))
      openIncident({
        incidentId: dataResponse?.data.createIncident.incident.id,
        displayId: dataResponse?.data.createIncident.incident.displayId,
      })
      trackEvent(INCIDENT_CREATE_ANALYTICS.incidentRowPress, {
        incident_id: dataResponse?.data.createIncident.incident.displayId,
        incident_status: data?.status,
        incident_type: data?.type,
      })
    } catch (error) {
      Sentry.captureException(error)
      showError(t('incidents:create_incident_error'))
    }
  }

  return (
    <Modal
      bodyStyleOverrides={{ minH: 0, overflowY: 'auto' }}
      closeOnEsc={!isDirty}
      closeOnOverlayClick={!isDirty}
      colorScheme={colorScheme}
      isOpen={isOpen}
      isSaveDisabled={!isValid || !shouldEnableCreateIncident}
      isSaveLoading={isLoading}
      maxW='600px'
      onClose={onClose}
      onSave={form?.handleSubmit(onSubmit)}
      saveButtonLabel={t('incidents:create_incident')}
      showCancelButton
      size='compact'
      title={t('incidents:create_incident')}
    >
      <DarkIncidentModalBody
        colorScheme={colorScheme}
        form={form}
        onFileRemove={removeFile}
        onFileSelect={handleFileSelect}
        selectedAttachments={selectedFiles}
      />
    </Modal>
  )
}
